import React, { useCallback, useMemo, type FC } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import Heading from '@atlaskit/heading/heading';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Box } from '@atlaskit/primitives/compiled/box';
import SectionMessage from '@atlaskit/section-message/message';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { APPROVALS_ANALYTICS_SOURCE } from '@confluence/approvals/entry-points/analytics.ts';
import { openApprovalActivityPaneForContext } from '@confluence/approvals/entry-points/approvalActivityPaneController.ts';
import { useApprovalActivityHistory } from '@confluence/approvals/entry-points/useApprovalActivityHistory.ts';
import type {
	ApprovalStatus,
	WorkflowStatus,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_PANE_ID,
	openApprovalsPaneForContext,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { ContentState } from '../EditorContentState/types.ts';
import { ContentStateColor } from '../ContentStateColor.tsx';
import {
	ContentStatePopupBase,
	ChangeStatusButton,
	PopupFooterButton,
} from '../ContentStatePopupBase/ContentStatePopupBase.tsx';

import { getRelativeTimeAgo } from './getRelativeTimeAgo.ts';
import { UserUpdateInfo, type UserUpdateInfoProps } from './UserUpdateInfo.tsx';
import { StartApprovalButton } from './ApprovalStatusButton/StartApprovalButton.tsx';
import { LiveDocStartApprovalPopup } from './ApprovalStatusButton/LiveDocStartApprovalPopup.tsx';
import { ApprovalStatusButton } from './ApprovalStatusButton/ApprovalStatusButton.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import {
	InProgressApprovalButton,
	InProgressApprovalButtonVariant,
} from './ApprovalStatusButton/InProgressApprovalButton.tsx';

const styles = cssMap({
	statusInfo: {
		padding: token('space.200'),
	},
	statusHeader: {
		width: '100%',
	},
	updateMetadata: {
		flexWrap: 'wrap',
	},
	otherModeWarningWrapper: {
		marginBlockEnd: token('space.150'),
		marginInline: token('space.200'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
	},
});

const i18n = defineMessages({
	cancelApproval: {
		id: 'content-state.contentStateInfoPopup.cancel-approval-button.text',
		defaultMessage: 'Cancel approval',
		description: 'Text for button which cancels the active approval request',
	},
	updatedByUser: {
		id: 'content-state.contentStateInfoPopup.updated-by-user',
		defaultMessage: 'Updated {time} by',
		description:
			'Shows when a status was last updated with relative time (including "ago"). User component is rendered separately.',
	},
	updated: {
		id: 'content-state.contentStateInfoPopup.updated',
		defaultMessage: 'Updated {time}',
		description: 'Shows when a status was last updated with relative time (including "ago") only',
	},
	updatedOnByUser: {
		id: 'content-state.contentStateInfoPopup.updated-on-by-user',
		defaultMessage: 'Updated on {date} by',
		description:
			'Shows when a status was last updated with absolute date and user who updated it (for dates older than 7 days). User component is rendered separately.',
	},
	updatedOn: {
		id: 'content-state.contentStateInfoPopup.updated-on',
		defaultMessage: 'Updated on {date}',
		description:
			'Shows when a status was last updated with absolute date only (for dates older than 7 days)',
	},
	updatedByUserOnly: {
		id: 'content-state.contentStateInfoPopup.updated-by-user-only',
		defaultMessage: 'Updated by',
		description:
			'Shows when a user updated a status (when time data is not available). User component is rendered separately.',
	},
	otherModeReviewWarningEdit: {
		id: 'content-state.contentStateInfoPopup.other-mode-review-warning-edit',
		defaultMessage:
			'The published version of this page is under review. Complete or cancel that review in order to start a new one here in the editor.',
		description:
			'Warning message shown in the editor when the published version of the page has an active approval review.',
	},
	otherModeReviewWarningView: {
		id: 'content-state.contentStateInfoPopup.other-mode-review-warning-view',
		defaultMessage:
			'The draft version of this page is under review. Complete or cancel that review in order to start a new one here in the published view.',
		description:
			'Warning message shown in the published view when the draft version of the page has an active approval review.',
	},
	approvalNeedsAttention: {
		id: 'content-state.contentStateInfoPopup.approval-needs-attention',
		defaultMessage: 'Approval needs attention',
		description:
			'Button label shown when space approval requirements changed and the approval pane needs to be opened.',
	},
	viewApprovalHistory: {
		id: 'content-state.contentStateInfoPopup.view-approval-history',
		defaultMessage: 'View approval history',
		description: 'Button label that opens the approval activity history panel.',
	},
});

type ContentStateInfoPopupProps = {
	contentState: ContentState | null;
	activeWorkflowStatus?: WorkflowStatus | null;
	updatedBy?: UserUpdateInfoProps;
	updatedAt?: string;
	loading?: boolean;
	onChangeStatus?: () => void;
	onClearRequest?: () => void;
	onClose?: () => void;
	onConvertLiveDocToPage?: () => void;
	canEdit?: boolean;
	contentType?: 'page' | 'blogpost' | null;
	isViewMode?: boolean;
};

export const ContentStateInfoPopup: FC<ContentStateInfoPopupProps> = ({
	contentState,
	activeWorkflowStatus,
	updatedBy,
	updatedAt,
	loading = false,
	onChangeStatus,
	onClearRequest,
	onClose,
	onConvertLiveDocToPage,
	canEdit = true,
	contentType,
	isViewMode = false,
}) => {
	const intl = useIntl();
	const { cloudId, edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		approvalRequest,
		hasEditPermission,
		approvalProgress,
		otherModeReviewStatus,
		contentId,
		contentStatus,
		availableWorkflowStatuses,
		isPendingReviewBlockedByRequiredApproversChange,
	} = useApprovals();
	const isLivePage = useIsCurrentPageLive();
	const [showLiveDocStartApprovalPopup, setShowLiveDocStartApprovalPopup] = React.useState(false);

	const approvers = approvalRequest?.review?.approvers;
	const rightSidebarContext = useRightSidebarContext();
	const liveDocApprovalWorkflowStatus = availableWorkflowStatuses.find(
		(status) => status.allowsReviews,
	);

	const handleApprovalStatusButtonClick = useCallback(() => {
		if (rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID) {
			onClose?.();
			return;
		}

		openApprovalsPaneForContext({
			contentId,
			contentStatus,
			rightSidebarContext,
		});
	}, [contentId, contentStatus, onClose, rightSidebarContext]);

	const handleStartApprovalClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'startApproval',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					contentStatus,
				},
			},
		}).fire();
		if (isLivePage && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			setShowLiveDocStartApprovalPopup(true);
			return;
		}

		handleApprovalStatusButtonClick();
	}, [createAnalyticsEvent, contentId, contentStatus, handleApprovalStatusButtonClick, isLivePage]);

	const handleViewApprovalHistoryClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewApprovalHistory',
				source: APPROVALS_ANALYTICS_SOURCE,
				attributes: {
					contentId,
					contentStatus,
				},
			},
		}).fire();
		onClose?.();
		openApprovalActivityPaneForContext({
			contentId,
			contentStatus,
			rightSidebarContext,
		});
	}, [createAnalyticsEvent, contentId, contentStatus, onClose, rightSidebarContext]);

	const handleConvertLiveDocToPage = useCallback(() => {
		try {
			onConvertLiveDocToPage?.();
		} finally {
			onClose?.();
		}
	}, [onClose, onConvertLiveDocToPage]);

	const handleBackFromLiveDocStartApprovalPopup = useCallback(() => {
		setShowLiveDocStartApprovalPopup(false);
	}, []);

	const isActiveReview =
		!!approvalRequest?.review && approvalRequest?.review?.reviewStatus !== 'CANCELED';
	const approvalStatus = approvalRequest?.review?.reviewStatus;
	const isPendingReviewBlockedByRequiredApproversChangeForCurrentReview =
		isActiveReview &&
		approvalStatus === 'PENDING' &&
		isPendingReviewBlockedByRequiredApproversChange;

	const hasOtherModePendingReview = otherModeReviewStatus === 'PENDING';
	const canStartApproval =
		Boolean(activeWorkflowStatus?.allowsReviews) ||
		Boolean(
			isLivePage &&
			liveDocApprovalWorkflowStatus &&
			onConvertLiveDocToPage &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true),
		);
	const wouldShowStartApproval = !isActiveReview && canStartApproval && hasEditPermission;
	const showOtherModeWarning = hasOtherModePendingReview && wouldShowStartApproval;
	let approvalActivityContentStatus: typeof contentStatus = 'CURRENT';
	let shouldFetchApprovalActivity =
		isPremium &&
		Boolean(contentId) &&
		contentStatus === 'CURRENT' &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);
	if (fg('cc_approvals_activity_log_edit_mode')) {
		approvalActivityContentStatus = contentStatus;
		shouldFetchApprovalActivity =
			isPremium &&
			Boolean(contentId) &&
			expValEquals('confluence_approvals_eap', 'isEnabled', true);
	}
	const { items: approvalActivityItems } = useApprovalActivityHistory({
		cloudId,
		contentId,
		contentStatus: approvalActivityContentStatus,
		first: 1,
		skip: !shouldFetchApprovalActivity,
	});
	const showApprovalActivityEntryPoint =
		shouldFetchApprovalActivity && approvalActivityItems.length > 0;

	const approvalCount = useMemo(
		() => (approvers ?? []).filter((a) => a.status === 'REVIEWER_APPROVED').length,
		[approvers],
	);

	const displayName = activeWorkflowStatus?.name ?? contentState?.name;
	const timeData = updatedAt ? getRelativeTimeAgo({ lastRelevantDate: updatedAt, intl }) : null;
	const shouldHideRecentUpdateMetadata = timeData?.type === 'relative' && timeData.value === 'now';

	const approvalStatusToVariant: Partial<Record<ApprovalStatus, InProgressApprovalButtonVariant>> =
		{
			PENDING: InProgressApprovalButtonVariant.IN_PROGRESS,
			APPROVED: InProgressApprovalButtonVariant.APPROVED,
			DENIED: InProgressApprovalButtonVariant.CHANGES_REQUESTED,
		};

	const StatusButton = () => {
		if (!isPremium) return null;

		if (isActiveReview) {
			if (isPendingReviewBlockedByRequiredApproversChangeForCurrentReview) {
				return (
					<ApprovalStatusButton appearance="warning" onClick={handleApprovalStatusButtonClick}>
						<Inline space="space.100" alignBlock="center">
							<StatusWarningIcon label="" color={token('color.icon.warning')} />
							<Text size="medium" weight="bold">
								{intl.formatMessage(i18n.approvalNeedsAttention)}
							</Text>
						</Inline>
					</ApprovalStatusButton>
				);
			}

			const variant = approvalStatus ? approvalStatusToVariant[approvalStatus] : undefined;
			if (!variant) {
				return <></>;
			}
			return (
				<InProgressApprovalButton
					approvalCount={approvalCount}
					approverCount={approvalProgress.total || 0}
					variant={variant}
					onClick={handleApprovalStatusButtonClick}
					approvers={approvers ?? []}
				/>
			);
		}

		if (showLiveDocStartApprovalPopup) {
			return (
				<LiveDocStartApprovalPopup
					onBack={handleBackFromLiveDocStartApprovalPopup}
					onConvertToPage={handleConvertLiveDocToPage}
				/>
			);
		}

		if (canStartApproval && hasEditPermission && !showOtherModeWarning) {
			return <StartApprovalButton onClick={handleStartApprovalClick} />;
		}

		return null;
	};

	const renderStatusIcon = () => {
		if (activeWorkflowStatus) {
			const isChangesRequested = activeWorkflowStatus.id === '3';
			let WorkflowIcon;
			let iconColor;
			if (isChangesRequested) {
				WorkflowIcon = StatusWorkflowReturnedIcon;
				iconColor = token('color.icon.accent.yellow');
			} else if (activeWorkflowStatus.allowsReviews) {
				WorkflowIcon = StatusWorkflowInReviewIcon;
				iconColor = token('color.icon.accent.blue');
			} else {
				WorkflowIcon = CheckCircleIcon;
				iconColor = token('color.icon.accent.lime');
			}
			return <WorkflowIcon label="" color={iconColor} />;
		}
		return <ContentStateColor color={contentState?.color} appearance="contentStateInfoPopup" />;
	};

	return (
		<ContentStatePopupBase>
			{/* Status Header and Metadata Section */}
			<Stack xcss={styles.statusInfo} space="space.100">
				{/* Status Header: Icon + Status Name */}
				<Inline alignBlock="center" spread="space-between" xcss={styles.statusHeader}>
					<Inline space="space.100" alignBlock="center">
						{renderStatusIcon()}
						<Heading as="h2" size="medium">
							{displayName}
						</Heading>
					</Inline>
					{showApprovalActivityEntryPoint && (
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={HistoryIcon}
							label={intl.formatMessage(i18n.viewApprovalHistory)}
							onClick={handleViewApprovalHistoryClick}
							testId="approval-activity-entry-point-button"
						/>
					)}
				</Inline>

				{/* User/Timestamp Metadata - flexible layout */}
				{!shouldHideRecentUpdateMetadata && (timeData || updatedBy || loading) && (
					<Inline
						space="space.050"
						alignBlock="center"
						alignInline={loading ? 'center' : 'start'}
						xcss={styles.updateMetadata}
					>
						{loading ? (
							<Spinner size="small" interactionName="content-state-info-popup" />
						) : (
							<>
								{/* Case 1: Both time and user data (Relative) */}
								{timeData && updatedBy && timeData.type === 'relative' && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedByUser, {
												time: timeData.value,
											})}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}

								{/* Case 2: Both time and user data (Absolute) */}
								{timeData && updatedBy && timeData.type === 'absolute' && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedOnByUser, {
												date: timeData.value,
											})}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}

								{/* Case 3: Time data only (Relative) */}
								{timeData && !updatedBy && timeData.type === 'relative' && (
									<Text size="small" color="color.text.subtlest">
										{intl.formatMessage(i18n.updated, {
											time: timeData.value,
										})}
									</Text>
								)}

								{/* Case 4: Time data only (Absolute) */}
								{timeData && !updatedBy && timeData.type === 'absolute' && (
									<Text size="small" color="color.text.subtlest">
										{intl.formatMessage(i18n.updatedOn, {
											date: timeData.value,
										})}
									</Text>
								)}

								{/* Case 5: User data only */}
								{!timeData && updatedBy && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedByUserOnly)}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}
							</>
						)}
					</Inline>
				)}
			</Stack>

			{showOtherModeWarning && (
				<Box xcss={styles.otherModeWarningWrapper}>
					<SectionMessage appearance="warning">
						{intl.formatMessage(
							isViewMode ? i18n.otherModeReviewWarningView : i18n.otherModeReviewWarningEdit,
						)}
					</SectionMessage>
				</Box>
			)}

			{!(isViewMode && contentType === 'blogpost') && <StatusButton />}

			{/* Change Status / Clear Request Button - only shown for users with edit permission, hidden for blog posts in view mode */}
			{canEdit &&
				!(isViewMode && contentType === 'blogpost') &&
				(isActiveReview && approvalStatus === 'PENDING' && onClearRequest ? (
					<PopupFooterButton onClick={onClearRequest} testId="cancel-approval-footer-button">
						<Text weight="medium" color="color.text.subtle">
							{intl.formatMessage(i18n.cancelApproval)}
						</Text>
					</PopupFooterButton>
				) : (
					<ChangeStatusButton onClick={onChangeStatus} />
				))}
		</ContentStatePopupBase>
	);
};
